/**
 * ExtractionService
 *
 * This service is responsible for managing and broadcasting extraction-related data
 * across the application. It uses RxJS BehaviorSubjects to store and emit data such as:
 *
 * 1. Extracted Data: The main data extracted from a source
 * 2. Confidence Data: Confidence scores for the extracted data.
 * 3. Edited Changes: Tracks user edits to the extracted data.
 * 4. Original Extraction Copy: A backup copy of the original extracted data.
 *
 */
import { Injectable, NgZone, OnDestroy, Inject } from "@angular/core";
import { BehaviorSubject, Subject, filter, takeUntil } from "rxjs";
import { AppConfigService } from "src/app/services/app-config.service";
import { MESSAGES } from "src/app/shared/constants/messages.constant";
import { RetryService } from "./retry.service";
import { UserService } from "./user.service";
import { MatDialog } from "@angular/material/dialog";
import { MmpConfirmPopupComponent } from "src/app/components/mmp-confirm-popup/mmp-confirm-popup.component";
import { Router } from "@angular/router";
import { ComparisonNormalizer } from "src/app/shared/utils/comparison-normalizer.util";
import { OKTA_AUTH } from "@okta/okta-angular";
import OktaAuth from "@okta/okta-auth-js";

export interface TranscriptResult {
  sessionKey: string;
  status: "started" | "completed";
  output: any;
  checkpoint: string;
  eligibleForProceeding?: boolean;
}

@Injectable({
  providedIn: "root",
})
export class ExtractionService implements OnDestroy {
  //extraction data
  private readonly extractedDataSubject = new BehaviorSubject<any>(null);
  extractedData$ = this.extractedDataSubject.asObservable();

  //confidence data
  private readonly confidenceDataSubject = new BehaviorSubject<any>(null);
  confidencedData$ = this.confidenceDataSubject.asObservable();

  //edited changes data
  private readonly editChangesSubject = new BehaviorSubject<any>({
    student: {},
    college: {},
    course: {},
  });
  editChanges$ = this.editChangesSubject.asObservable();

  //confidence changes data
  private readonly confidenceChangesSubject = new BehaviorSubject<any>({
    student: {},
    college: {},
    course: {},
  });
  confidenceChanges$ = this.confidenceChangesSubject.asObservable();

  //orginal extraction copy
  private readonly extractedDataCopySubject = new BehaviorSubject<any>(null);
  extractedCopy$ = this.extractedDataCopySubject.asObservable();

  private socket: WebSocket;
  private readonly extractionWebsocketUrl: string = "";
  private readonly showConsoleLogs: boolean = false;
  private combinedId: string = "";
  private extractionSource: string = "";
  private caseNumber: string = "";

  private readonly transcriptMap = new Map<string, TranscriptResult>();
  private readonly transcriptsSubject = new BehaviorSubject<TranscriptResult[]>(
    [],
  );

  transcripts$ = this.transcriptsSubject.asObservable();

  private readonly extractionSummarySubject = new BehaviorSubject<any>(null);
  extractedSummary$ = this.extractionSummarySubject.asObservable();

  private readonly studentDetailsSubject = new BehaviorSubject<any>(null);
  studentDetails$ = this.studentDetailsSubject.asObservable();

  private readonly hiddenIndexesEvaluationSubject = new BehaviorSubject<
    number[]
  >([]);
  hiddenIndexesEvaluation$ = this.hiddenIndexesEvaluationSubject.asObservable();

  private readonly transcriptVersionMap = new Map<string, any>();

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly envService: AppConfigService,
    private readonly zone: NgZone,
    private readonly retryService: RetryService,
    private readonly userService: UserService,
    private readonly dialog: MatDialog,
    private readonly router: Router,
    @Inject(OKTA_AUTH) private readonly oktaAuth: OktaAuth,
  ) {
    this.extractionWebsocketUrl = this.envService.mmpExtractionWebsocketUrl;
    this.showConsoleLogs = this.envService.showConsoleLogs;
    this.getRetryResults();
  }

  //Dummy websocket start
  startWebSocket(
    data: any,
    combinedId: string,
    source: string,
    caseNumber: string,
  ) {
    this.combinedId = combinedId;
    this.extractionSource = source;
    this.caseNumber = caseNumber;

    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.close();
    }
    this.socket = new WebSocket(this.extractionWebsocketUrl);

    this.socket.onopen = async () => {
      const accessToken = this.oktaAuth.getAccessToken();

      this.socket.send(
        JSON.stringify({
          type: "auth",
          token: accessToken,
        }),
      );

      const payload = Array.isArray(data)
        ? data.map((item) => ({
            ...item,
            combined_id: combinedId,
            case_number: caseNumber,
            source: source,
            user_name: this.userService.getUser().name,
          }))
        : {
            ...data,
            combined_id: combinedId,
            case_number: caseNumber,
            source: source,
            user_name: this.userService.getUser().name,
          };

      this.socket.send(JSON.stringify(payload));

      const intervalId = setInterval(() => {
        if (this.socket.readyState === WebSocket.OPEN) {
          this.socket.send(
            JSON.stringify({
              type: MESSAGES.WEBSOCKET.HANDSHAKE_TYPE,
              text: MESSAGES.WEBSOCKET.UPLOAD_HANDSHAKE_MESSAGE,
            }),
          ); //Handshake senting to backend(server)
          if (this.showConsoleLogs) {
            console.log(MESSAGES.WEBSOCKET.FIXED_MESSAGE_SENT);
          }
        } else {
          if (this.showConsoleLogs) {
            console.log(MESSAGES.WEBSOCKET.HANDSHAKE_STOPED);
          }

          clearInterval(intervalId);
        }
      }, 10000);
    };

    this.socket.onmessage = (event) => {
      // Ensure Angular change detection runs
      this.zone.run(() => this.handleMessage(event));
    };

    this.socket.onerror = (error) => {
      if (this.showConsoleLogs) {
        console.error("WebSocket error:", error);
      }
    };

    this.socket.onclose = (event) => {
      const currentValue = this.getTranscriptMapValues();
      if (event.wasClean) {
        this.handleCleanClose(event, currentValue);
      } else {
        this.handleUnexpectedClose(event, currentValue, data);
      }
    };
  }

  private handleCleanClose(event: CloseEvent, currentValue: any[]): void {
    if (this.showConsoleLogs) {
      console.log(MESSAGES.WEBSOCKET.CLOSED_CLEAN, event.code, event.reason);
    }

    if (
      ComparisonNormalizer.string(event.reason) === "keepalive ping timeout"
    ) {
      this.retryService.checkRetryRequired(currentValue, "extraction");
    }
  }

  private handleUnexpectedClose(
    event: CloseEvent,
    currentValue: any[],
    uploadData: any,
  ): void {
    if (this.showConsoleLogs) {
      console.warn(
        MESSAGES.WEBSOCKET.CLOSED_UNEXPECTED,
        event.code,
        event.reason,
      );
    }

    if (currentValue.length === 0) {
      if (this.showConsoleLogs) {
        console.log("Websocket closed unexpectedly, retry upload ws");
      }
      this.extractionRetry(uploadData);
    } else {
      this.retryService.checkRetryRequired(currentValue, "extraction");
    }
  }

  private handleMessage(event: MessageEvent) {
    const data = JSON.parse(event.data);
    const sessionKey = data.session_key;

    this.processStartedStatus(data.status, sessionKey);
    this.processExtractionOutput(data, sessionKey);
    this.processExtractionCheckpoint(data, sessionKey);
    this.processFinalSummary(data);
    this.processDuplicate(data);
    this.handleErrorStatus(data.status);
    this.handleCompletedStatus(data.status);
  }

  public processStartedStatus(status: string, sessionKey: string): void {
    if (ComparisonNormalizer.string(status) === "started") {
      this.transcriptMap.set(sessionKey, {
        sessionKey,
        status: "started",
        output: "",
        checkpoint: "",
        eligibleForProceeding: true,
      });
    }
  }

  public processExtractionOutput(data: any, sessionKey: string): void {
    if (ComparisonNormalizer.string(data.node) !== "extraction_outputs") {
      return;
    }

    const transcript = this.transcriptMap.get(sessionKey);
    if (transcript) {
      transcript.status = "completed";
      transcript.output = data.output;
    }
    this.emit();
  }

  public processExtractionCheckpoint(data: any, sessionKey: string): void {
    if (
      ComparisonNormalizer.string(data.checkpoint_type) !==
      "extraction_checkpoint"
    ) {
      return;
    }

    const transcript = this.transcriptMap.get(sessionKey);
    if (transcript) {
      transcript.checkpoint = data.extraction_checkpoint_id;
    }
  }

  public processFinalSummary(data: any): void {
    if (ComparisonNormalizer.string(data.type) === "final_summary") {
      this.extractionSummarySubject.next(data.data);
    }
  }
  public processDuplicate(data: any): void {
    if (ComparisonNormalizer.string(data.type) === "transcript_version") {
      const transcriptsData = data.data;

      for (const transcriptData of transcriptsData) {
        const sessionKey = transcriptData.session_id;
        const transcript = this.transcriptMap.get(sessionKey);
        if (transcript) {
          const classification = ComparisonNormalizer.string(
            transcriptData.classification,
          ).replaceAll(/\s+/g, "_");
          transcript.output.result.college.transcript_version = classification;
        }
        this.transcriptVersionMap.set(sessionKey, transcriptData);
      }
      this.emit();
    }
  }

  public setTranscriptVersion(sessionKey: string, version: string): void {
    const transcript = this.transcriptVersionMap.get(sessionKey);
    if (transcript) {
      const readableVersion = version.replaceAll("_", " ");
      transcript.classification = readableVersion;
    }
  }

  public getTranscriptVersion(): Map<string, any> {
    return this.transcriptVersionMap;
  }

  public handleErrorStatus(status: string): void {
    if (ComparisonNormalizer.string(status) === "error") {
      this.disconnect();
      this.showErrorDialog("An error occurred during extraction.");
    }
  }

  private handleCompletedStatus(status: string): void {
    if (ComparisonNormalizer.string(status) !== "completed") {
      return;
    }

    const allCompleted = Array.from(this.transcriptMap.values()).every(
      (transcript) =>
        ComparisonNormalizer.string(transcript.status) === "completed",
    );

    if (allCompleted) {
      if (this.showConsoleLogs) {
        console.log("All transcripts are completed. Closing WebSocket...");
      }
      this.disconnect();
      return;
    }

    if (this.showConsoleLogs) {
      console.log(MESSAGES.RETRY.RETRY_REQUIRED);
    }
    this.disconnect();
    const currentValue = this.getTranscriptMapValues();
    this.retryService.checkRetryRequired(currentValue, "extraction");
  }

  private emit() {
    this.transcriptsSubject.next(Array.from(this.transcriptMap.values()));
  }

  disconnect() {
    this.socket?.close();
  }

  setExtractionData(data: any) {
    this.extractedDataSubject.next(data);
  }

  getExtractionData() {
    return this.extractedDataSubject.value;
  }

  setConfidenceData(data: any) {
    this.confidenceDataSubject.next(data);
  }

  getConfidenceData() {
    return this.confidenceDataSubject.value;
  }

  setEditChanges(data: any) {
    this.editChangesSubject.next(data);
  }

  getEditChanges() {
    return this.editChangesSubject.value;
  }

  setConfidenceChanges(data: any) {
    this.confidenceChangesSubject.next(data);
  }

  getConfidenceChanges() {
    return this.confidenceChangesSubject.value;
  }

  setExtractionCopy(data: any) {
    const cloned = data ? structuredClone(data) : data;
    this.applySavedChangesToExtractionCopy(cloned);
    this.applySavedConfidenceChangesToExtractionCopy(cloned);
    this.extractedDataCopySubject.next(cloned);
  }

  private applySavedChangesToExtractionCopy(cloned: any): void {
    if (!Array.isArray(cloned?.transcripts)) {
      return;
    }

    const accumulatedChanges = {
      student: {},
      college: {},
      course: {},
    };
    let savedChanges;

    cloned.transcripts.forEach((transcript: any, transcriptIndex: number) => {
      savedChanges = transcript?.result?.frontend_data?.savedChanges;

      if (!savedChanges) {
        return;
      }

      // Accumulate student changes
      if (savedChanges.student && typeof savedChanges.student === "object") {
        accumulatedChanges.student = {
          ...accumulatedChanges.student,
          ...savedChanges.student,
        };
      }

      // Accumulate college changes with transcript index
      if (savedChanges.college && typeof savedChanges.college === "object") {
        accumulatedChanges.college[transcriptIndex] = {
          ...accumulatedChanges.college[transcriptIndex],
          ...savedChanges.college,
        };
      }

      // Accumulate course changes with transcript index
      if (savedChanges.course && typeof savedChanges.course === "object") {
        accumulatedChanges.course[transcriptIndex] = {
          ...accumulatedChanges.course[transcriptIndex],
          ...savedChanges.course,
        };
      }

      this.applySavedStudentChangesToExtractionCopy(
        cloned,
        savedChanges.student,
      );
      this.applySavedCollegeFieldsToTranscript(
        transcript,
        savedChanges.college,
      );
      this.applySavedCourseFieldsToTranscript(transcript, savedChanges.course);
    });

    if (savedChanges) {
      // Set the accumulated changes to the editChanges subject
      this.setEditChanges(accumulatedChanges);
    }
  }

  private applySavedStudentChangesToExtractionCopy(
    cloned: any,
    savedStudentChanges: any,
  ): void {
    if (!savedStudentChanges || typeof savedStudentChanges !== "object") {
      return;
    }

    cloned.student_information = cloned.student_information || {};

    for (const [field, value] of Object.entries(savedStudentChanges)) {
      cloned.student_information[field] = value;
    }
  }

  private applySavedCollegeFieldsToTranscript(
    transcript: any,
    savedCollegeChanges: any,
  ): void {
    if (
      !savedCollegeChanges ||
      typeof savedCollegeChanges !== "object" ||
      !transcript?.college
    ) {
      return;
    }

    for (const [field, value] of Object.entries(savedCollegeChanges)) {
      if (ComparisonNormalizer.string(field) === "college_accreditation") {
        transcript.college.college_accreditation =
          transcript.college.college_accreditation || {};
        transcript.college.college_accreditation.is_accredited = value;
        continue;
      }

      transcript.college[field] = value;
    }
  }

  private applySavedCourseFieldsToTranscript(
    transcript: any,
    savedCourseChanges: any,
  ): void {
    if (
      !savedCourseChanges ||
      typeof savedCourseChanges !== "object" ||
      !Array.isArray(transcript?.courses)
    ) {
      return;
    }

    for (const [courseIndex, courseChanges] of Object.entries(
      savedCourseChanges,
    )) {
      const targetCourse = transcript.courses[Number(courseIndex)];

      if (
        !targetCourse ||
        !courseChanges ||
        typeof courseChanges !== "object"
      ) {
        continue;
      }

      for (const [field, value] of Object.entries(courseChanges)) {
        targetCourse[field] = value;
      }
    }
  }

  private applySavedConfidenceChangesToExtractionCopy(cloned: any): void {
    if (!Array.isArray(cloned?.transcripts)) {
      return;
    }

    const accumulatedConfidenceChanges = {
      student: {},
      college: {},
      course: {},
    };

    cloned.transcripts.forEach((transcript: any, transcriptIndex: number) => {
      const confidenceData =
        transcript?.result?.frontend_data?.savedConfidenceChanges;

      if (!confidenceData || typeof confidenceData !== "object") {
        return;
      }

      // Accumulate student confidence changes
      if (
        confidenceData.student &&
        typeof confidenceData.student === "object"
      ) {
        accumulatedConfidenceChanges.student = {
          ...accumulatedConfidenceChanges.student,
          ...confidenceData.student,
        };
      }

      // Accumulate college confidence changes with transcript index
      if (
        confidenceData.college &&
        typeof confidenceData.college === "object"
      ) {
        accumulatedConfidenceChanges.college[transcriptIndex] = {
          ...accumulatedConfidenceChanges.college[transcriptIndex],
          ...confidenceData.college,
        };
      }

      // Accumulate course confidence changes with transcript index and course index
      if (confidenceData.course && typeof confidenceData.course === "object") {
        accumulatedConfidenceChanges.course[transcriptIndex] =
          accumulatedConfidenceChanges.course[transcriptIndex] || {};

        for (const [courseIndex, courseConfidence] of Object.entries(
          confidenceData.course,
        )) {
          if (typeof courseConfidence === "object") {
            accumulatedConfidenceChanges.course[transcriptIndex][courseIndex] =
              {
                ...accumulatedConfidenceChanges.course[transcriptIndex][
                  courseIndex
                ],
                ...courseConfidence,
              };
          }
        }
      }
    });

    // Set the accumulated confidence changes to the confidenceChanges subject
    this.setConfidenceChanges(accumulatedConfidenceChanges);
  }

  getExtractionCopy() {
    const val = this.extractedDataCopySubject.value;
    return val ? structuredClone(val) : val;
  }

  setStudentInformations(data: any) {
    this.studentDetailsSubject.next(data);
  }

  getStudentInformations() {
    return this.studentDetailsSubject.value;
  }

  updateTranscriptOutput(
    sessionKey: string,
    newOutput: any,
    eligibleForProceeding?: boolean,
  ): void {
    // Check if the sessionKey exists in the transcriptMap
    const transcript = this.transcriptMap.get(sessionKey);
    if (!transcript) {
      if (this.showConsoleLogs) {
        console.error(`Transcript with sessionKey ${sessionKey} not found.`);
      }
      return;
    }

    // Update the output of the transcript
    transcript.output = newOutput;
    if (eligibleForProceeding !== undefined) {
      transcript.eligibleForProceeding = eligibleForProceeding;
    }
  }

  getTranscriptMapValues(): TranscriptResult[] {
    return Array.from(this.transcriptMap.values());
  }

  getTranscriptMap(): Map<string, TranscriptResult> {
    return this.transcriptMap;
  }

  clearExtractionResult() {
    this.transcriptMap.clear();
    this.transcriptsSubject.next([]);
    this.extractedDataSubject.next(null);
    this.confidenceDataSubject.next(null);
    this.editChangesSubject.next({
      student: {},
      college: {},
      course: {},
    });
    this.confidenceChangesSubject.next({
      student: {},
      college: {},
      course: {},
    });
    this.extractedDataCopySubject.next(null);
    this.extractionSummarySubject.next(null);
    this.studentDetailsSubject.next(null);
  }

  getRetryResults() {
    this.retryService.retryResult$
      .pipe(
        takeUntil(this.destroy$),
        filter(
          (result) => ComparisonNormalizer.string(result.type) === "extraction",
        ),
      )
      .subscribe((result) => {
        const data = result.payload;
        console.log("retry subscribe", data);
        const sessionKey = data.session_key;

        if (ComparisonNormalizer.string(data.node) === "extraction_outputs") {
          const transcript = this.transcriptMap.get(sessionKey);
          if (transcript) {
            transcript.status = "completed";
            transcript.output = data.output;
          }
          this.emit();
        }

        if (
          ComparisonNormalizer.string(data.checkpoint_type) ===
          "extraction_checkpoint"
        ) {
          const transcript = this.transcriptMap.get(sessionKey);
          if (transcript) {
            transcript.checkpoint = data.extraction_checkpoint_id;
          }
        }

        if (ComparisonNormalizer.string(data.type) === "final_summary") {
          this.extractionSummarySubject.next(data.data);
        }
      });
  }

  extractionRetry(data): void {
    this.retryService.showAgentRetryConfirmationDialog().then((response) => {
      if (ComparisonNormalizer.boolean(response)) {
        this.startWebSocket(
          data,
          this.combinedId,
          this.extractionSource,
          this.caseNumber,
        );
      }
    });
  }

  disconnectRetry(): void {
    this.retryService.disconnect();
  }

  showErrorDialog(errorMessage: string): void {
    const dialogRef = this.dialog.open(MmpConfirmPopupComponent, {
      width: "500px",
      disableClose: true,
      data: {
        title: errorMessage,
        message: MESSAGES.RETRY.RETRY_FAILED,
        confirmText: "Go to Dashboard",
        hideCancelButton: true,
        errorPopup: true,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (ComparisonNormalizer.string(result) === "yes") {
        // Navigate to dashboard
        this.router.navigate(["/evaluator-dashboard"]);
      }
    });
  }

  setSavedHiddenIndexesEvaluation(): void {
    const hiddenIndexes = Array.from(this.transcriptMap.values())
      .map((transcript: TranscriptResult, index: number) => {
        return transcript?.output?.result?.frontend_data
          ?.eligibleForProceeding === false
          ? index
          : -1;
      })
      .filter((index: number) => index !== -1);

    this.hiddenIndexesEvaluationSubject.next(hiddenIndexes);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
